import {
    Box,
    Stack,
    Typography,
    IconButton,
    Chip,
    Skeleton,
} from "@compass/core";
import { Dialog, DialogContent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { NotificationDto } from "generated/openApi";
import { AppTopBarNotificationsCard } from "./AppTopBarNotificationsCard";

interface AppTopBarNotificationsModalProps {
    open: boolean;
    loading: boolean;
    notifications: NotificationDto[];
    unreadCount: number;
    onClose: () => void;
}

export const AppTopBarNotificationsModal = ({
    open,
    loading,
    notifications,
    unreadCount,
    onClose,
}: AppTopBarNotificationsModalProps) => {
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: "80vh",
                    bgcolor: theme.palette.background.default,
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    mx: 3,
                    mt: 2,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    p: 3,
                    boxShadow: 2,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderLeftWidth: 6,
                    borderLeftColor: theme.palette.primary.main,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing="sm">
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: theme.palette.primary.dark, letterSpacing: 0.3 }}
                        >
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip
                                label={`${unreadCount} unread`}
                                size="small"
                                color="error"
                            />
                        )}
                    </Stack>

                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2 }}>
                    {loading ? (
                        <NotificationsLoadingSkeleton />
                    ) : notifications.length === 0 ? (
                        <NotificationsEmptyState />
                    ) : (
                        <Box sx={{ position: "relative" }}>
                            {/* Continuous timeline line */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: 20,
                                    top: 0,
                                    bottom: 0,
                                    width: 2,
                                    bgcolor: theme.palette.divider,
                                    zIndex: 0,
                                }}
                            />

                            {notifications.map((notification) => (
                                <AppTopBarNotificationsCard
                                    key={notification.id}
                                    notification={notification}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

const NotificationsLoadingSkeleton = () => {
    const theme = useTheme();

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                sx={{
                    position: "absolute",
                    left: 20,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: theme.palette.divider,
                    zIndex: 0,
                }}
            />
            {Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} sx={{ position: "relative", pb: 3, pl: 6 }}>
                    <Box
                        sx={{
                            position: "absolute",
                            left: 12,
                            top: 8,
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            bgcolor: theme.palette.border.dark,
                            border: "3px solid",
                            borderColor: theme.palette.background.paper,
                            zIndex: 1,
                        }}
                    />
                    <Skeleton variant="text" width="30%" height={18} />
                    <Box
                        sx={{
                            mt: 1,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                        }}
                    >
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="100%" height={16} sx={{ mt: 1 }} />
                        <Skeleton variant="text" width="85%" height={16} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const NotificationsEmptyState = () => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
            }}
        >
            <NotificationsNoneOutlinedIcon
                sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }}
            />
            <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
            >
                You're all caught up
            </Typography>
            <Typography
                variant="body2"
                sx={{ mt: 0.5, color: theme.palette.text.disabled }}
            >
                No new notifications.
            </Typography>
        </Box>
    );
};